import {
  createBrowserHistory,
  createHashHistory,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  type ParsedLocation,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { authStore } from "@/lib/auth-store";
import { isMobile } from "@/lib/config";
import { RootLayout, NotFound, RouteError } from "@/routes/__root";
import { HomePage } from "@/routes/index";
import { AboutPage } from "@/routes/about";
import { BlueprintPage } from "@/routes/blueprint";
import { AcademyPage } from "@/routes/academy";
import { MentorshipPage } from "@/routes/mentorship";
import { SourcingPage } from "@/routes/deal-sourcing";
import { DealAnalyzerPage } from "@/routes/deal-analyzer";
import { LoginPage } from "@/routes/auth/login";
import { SignupPage } from "@/routes/auth/signup";
import { ForgotPasswordPage } from "@/routes/auth/forgot-password";
import { ResendConfirmationPage } from "@/routes/auth/resend-confirmation";
import { ConfirmEmailPage } from "@/routes/auth/confirm-email";
import { ResetPasswordPage } from "@/routes/auth/reset-password";
import { DashboardPage } from "@/routes/account/dashboard";
import { BillingPage } from "@/routes/account/billing";
import { ProfilePage } from "@/routes/account/profile";
import { DeleteAccountPage } from "@/routes/account/delete-account";
import { ContactPage } from "@/routes/contact";
import {
  DataProcessingPage,
  DeleteAccountInfoPage,
  PrivacyPage,
  RefundPage,
  SubscriptionPolicyPage,
  TermsPage,
} from "@/routes/legal";

export interface RouterContext {
  queryClient: QueryClient;
}

const str = (v: unknown) => (typeof v === "string" && v.length ? v : undefined);

/** Login-only pages: anonymous visitors go to /login?redirect=<current location>. */
function requireAuth({ location }: { location: ParsedLocation }) {
  if (!authStore.isAuthenticated()) {
    throw redirect({ to: "/login", search: { redirect: location.href } });
  }
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

const route = <P extends string>(path: P, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const indexRoute = route("/", HomePage);
const aboutRoute = route("/about", AboutPage);
const blueprintRoute = route("/blueprint", BlueprintPage);
const dealSourcingRoute = route("/deal-sourcing", SourcingPage);
const contactRoute = route("/contact", ContactPage);

const academyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/academy",
  beforeLoad: requireAuth,
  component: AcademyPage,
});

const mentorshipRoute = route("/mentorship", MentorshipPage);

const dealAnalyzerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/deal-analyzer",
  beforeLoad: requireAuth,
  component: DealAnalyzerPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): { redirect?: string | undefined } => ({ redirect: str(s["redirect"]) }),
  component: LoginPage,
});

/** LoginUrl of the API's email templates (AppClientOptions:LoginUrl). */
const identityLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/identity/account/login",
  beforeLoad: () => {
    throw redirect({ to: "/login", search: { redirect: undefined } });
  },
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  validateSearch: (s: Record<string, unknown>): { redirect?: string | undefined } => ({ redirect: str(s["redirect"]) }),
  component: SignupPage,
});

const forgotPasswordRoute = route("/forgot-password", ForgotPasswordPage);

const resendConfirmationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/resend-confirmation",
  validateSearch: (s: Record<string, unknown>): { email?: string | undefined } => ({ email: str(s["email"]) }),
  component: ResendConfirmationPage,
});

export interface EmailLinkSearch {
  userId?: string | undefined;
  code?: string | undefined;
  email?: string | undefined;
  returnUrl?: string | undefined;
}

// Query parameter names are matched case-insensitively, like [SupplyParameterFromQuery] in the Blazor pages.
const emailLinkSearch = (s: Record<string, unknown>): EmailLinkSearch => {
  const lower = Object.fromEntries(Object.entries(s).map(([k, v]) => [k.toLowerCase(), v]));
  return {
    userId: str(lower["userid"]),
    code: str(lower["code"]),
    email: str(lower["email"]),
    returnUrl: str(lower["returnurl"]),
  };
};

// Exact routes used in the API's email links (AppClientOptions:*Url).
const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/identity/account/ConfirmEmail",
  validateSearch: emailLinkSearch,
  component: () => <ConfirmEmailPage change={false} />,
});

const confirmEmailChangeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/identity/account/ConfirmEmailChange",
  validateSearch: emailLinkSearch,
  component: () => <ConfirmEmailPage change />,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/identity/account/ResetPassword",
  validateSearch: emailLinkSearch,
  component: ResetPasswordPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardPage,
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  beforeLoad: requireAuth,
  validateSearch: (s: Record<string, unknown>): { checkout?: string | undefined } => ({ checkout: str(s["checkout"]) }),
  component: BillingPage,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  beforeLoad: requireAuth,
  component: ProfilePage,
});

const deleteAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account/delete",
  beforeLoad: requireAuth,
  component: DeleteAccountPage,
});

const legalRoutes = [
  route("/legal/terms", TermsPage),
  route("/legal/privacy", PrivacyPage),
  route("/legal/subscription", SubscriptionPolicyPage),
  route("/legal/refunds", RefundPage),
  route("/legal/data-processing", DataProcessingPage),
  route("/legal/delete-account", DeleteAccountInfoPage),
] as const;

const routeTree = rootRoute.addChildren([
  indexRoute,
  aboutRoute,
  blueprintRoute,
  academyRoute,
  mentorshipRoute,
  dealSourcingRoute,
  dealAnalyzerRoute,
  contactRoute,
  loginRoute,
  identityLoginRoute,
  signupRoute,
  forgotPasswordRoute,
  resendConfirmationRoute,
  confirmEmailRoute,
  confirmEmailChangeRoute,
  resetPasswordRoute,
  dashboardRoute,
  billingRoute,
  profileRoute,
  deleteAccountRoute,
  ...legalRoutes,
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    // Mobile (HybridWebView, files served from the app package): hash routing; web: clean URLs (needs SPA fallback).
    history: isMobile ? createHashHistory() : createBrowserHistory(),
    context: { queryClient },
    defaultPreload: "intent",
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
